import axios from "./_axios";
import { API } from "./apiEndPoints";

/**
 * Account access list (paginated). Omit queryParams for a large first page (e.g. email lookup).
 * Sends page/size plus offset/limit for APIs that use offset-based pagination.
 * @param {string|number} account_id
 * @param {{ page?: number, size?: number, search?: string, offset?: number, limit?: number }} [queryParams]
 */
export const getListofUser = (account_id, queryParams = {}) => {
  const page = queryParams.page ?? 1;
  const size = queryParams.size ?? 500;
  const offset = queryParams.offset ?? Math.max(0, (page - 1) * size);
  const limit = queryParams.limit ?? size;

  const params = {
    page,
    size,
    offset,
    limit,
  };
  if (queryParams.search != null && String(queryParams.search).trim() !== "") {
    params.search = String(queryParams.search).trim();
  }
  return axios()({
    method: "get",
    url: `${API.ACCOUNTList}${account_id}/access/`,
    params,
  });
};

/**
 * Normalizes paginated access list responses (v1/v2 may differ).
 * @returns {{ results: array, count: number }}
 */
export function normalizeAccessListPayload(raw) {
  if (raw == null) {
    return { results: [], count: 0 };
  }
  if (Array.isArray(raw)) {
    return { results: raw, count: raw.length };
  }
  const list = [raw.results, raw.items, raw.data].find((x) => Array.isArray(x));
  if (list) {
    const count = Number(raw.count ?? raw.total ?? raw.total_count ?? list.length);
    return { results: list, count: count || list.length };
  }
  if (raw.data && typeof raw.data === "object" && Array.isArray(raw.data.results)) {
    const r = raw.data.results;
    const count = Number(raw.data.count ?? raw.count ?? r.length);
    return { results: r, count: count || r.length };
  }
  // Single access object (v2 user-scoped GET often returns one row)
  if (raw.id != null && raw.user != null) {
    return { results: [raw], count: 1 };
  }
  return { results: [], count: 0 };
}

function normalizeAdminUserId(value) {
  if (value == null || value === "" || String(value).toLowerCase() === "undefined") {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? String(n) : null;
}

function userIdFromAdminAccessRow(row) {
  if (!row) return null;
  const u = row.user;
  if (u && typeof u === "object") {
    const id = u.id ?? u.data?.user?.id ?? u.data?.id;
    if (id != null) return normalizeAdminUserId(id);
  }
  if (typeof u === "number") return normalizeAdminUserId(u);
  if (row.user_id != null) return normalizeAdminUserId(row.user_id);
  return null;
}

function isAdminAccessOwnerRow(row) {
  if (!row) return false;
  if (row.is_owner === true) return true;
  const perm = String(row.admin_permission || row.adminPermission || "")
    .trim()
    .toLowerCase();
  return perm === "owner";
}

/**
 * Account list / activate responses may expose `owner` as an access id, not a user id.
 * Cancel-invite is `POST /v2/user/{user_id}/cancel-invite/`, so resolve a real user id first.
 *
 * @param {Record<string, any>} account Row from admin account list (includes id, owner, owner_email, …).
 * @returns {Promise<string|null>}
 */
export async function resolveAdminAccountOwnerUserIdForCancelInvite(account) {
  if (!account || account.id == null) return null;

  const explicitCandidates = [
    account.owner_user_id,
    account.owner_user?.id,
    typeof account.owner_user === "number" ? account.owner_user : null,
    account.ownerUserId,
    account.user_owner_id,
  ];
  for (const c of explicitCandidates) {
    const id = normalizeAdminUserId(c);
    if (id) return id;
  }

  const ownerAccessOrUserId = normalizeAdminUserId(account.owner);
  const ownerEmail = String(account.owner_email || "")
    .trim()
    .toLowerCase();

  try {
    const res = await getListofUser(account.id, { limit: 500, offset: 0 });
    const { results } = normalizeAccessListPayload(res?.data);
    const resolved = pickOwnerUserIdFromAccessResults(results, ownerAccessOrUserId, ownerEmail);
    if (resolved) return resolved;
  } catch (e) {
    console.warn("resolveAdminAccountOwnerUserIdForCancelInvite: access list failed", e);
  }

  return null;
}

/**
 * Resolve the owner's user id from a fetched account access list.
 * Tries (in order): direct access-id match, owner-email match, owner flag,
 * sole-row shortcut, then a nested-user-id match. Returns null when none apply.
 *
 * @param {Array<Record<string, any>>} results Access rows for the account.
 * @param {string|null} ownerAccessOrUserId Normalized owner access/user id, if any.
 * @param {string} ownerEmail Lowercased owner email, if any.
 * @returns {string|null}
 */
function pickOwnerUserIdFromAccessResults(results, ownerAccessOrUserId, ownerEmail) {
  if (ownerAccessOrUserId) {
    const byAccessId = results.find((r) => normalizeAdminUserId(r?.id) === ownerAccessOrUserId);
    const fromAccess = userIdFromAdminAccessRow(byAccessId);
    if (fromAccess) return fromAccess;
  }
  if (ownerEmail) {
    const byEmail = results.find((r) => {
      const e = String(
        r?.user?.data?.user?.email || r?.user?.email || r?.email || ""
      ).toLowerCase();
      return e && e === ownerEmail;
    });
    const fromEmail = userIdFromAdminAccessRow(byEmail);
    if (fromEmail) return fromEmail;
  }
  const ownerRow = results.find((r) => r?.is_owner === true || isAdminAccessOwnerRow(r));
  const fromOwnerFlag = userIdFromAdminAccessRow(ownerRow);
  if (fromOwnerFlag) return fromOwnerFlag;

  if (results.length === 1) {
    const soleUserId = userIdFromAdminAccessRow(results[0]);
    if (soleUserId) return soleUserId;
  }

  if (ownerAccessOrUserId) {
    const byNestedUser = results.some((r) => userIdFromAdminAccessRow(r) === ownerAccessOrUserId);
    if (byNestedUser) return ownerAccessOrUserId;
  }
  return null;
}

/**
 * Access rows for one user under an account (legacy account-scoped path).
 * GET api/common/v2/account/{account_id}/user/{user_id}/access/
 */
export const getUserAccountAccess = (account_id, user_id, queryParams = {}) => {
  const page = queryParams.page ?? 1;
  const size = queryParams.size ?? 500;
  const offset = queryParams.offset ?? Math.max(0, (page - 1) * size);
  const limit = queryParams.limit ?? size;

  const params = {
    page,
    size,
    offset,
    limit,
  };
  if (queryParams.search != null && String(queryParams.search).trim() !== "") {
    params.search = String(queryParams.search).trim();
  }
  return axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/user/${user_id}/access/`,
    params,
  });
};

/**
 * Accounts / access rows for a user (admin Users list drill-down).
 * GET api/common/v2/user/{user_id}/account/
 */
export const getUserAccountsForUser = (user_id, queryParams = {}) => {
  const page = queryParams.page ?? 1;
  const size = queryParams.size ?? 500;
  const offset = queryParams.offset ?? Math.max(0, (page - 1) * size);
  const limit = queryParams.limit ?? size;

  const params = {
    page,
    size,
    offset,
    limit,
  };
  if (queryParams.search != null && String(queryParams.search).trim() !== "") {
    params.search = String(queryParams.search).trim();
  }
  return axios()({
    method: "get",
    url: `${API.USER_LIST_V2}${user_id}/account/`,
    params,
  });
};

export const createUser = (data) => {
  let result = axios()({
    method: "post",
    url: `${API.USERList}`,
    data: data,
  });
  return result;
};

export const createAdminUser = (data) => {
  let result = axios()({
    method: "post",
    url: `${API.ADMINUserCreate}`,
    data: data,
  });
  return result;
};

export const manageAppAccess = (account_id, user_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/user/${user_id}/access/`,
    data: data,
  });
  return result;
};

export const getAppAccess = (account_id, user_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/user/${user_id}/access/`,
    data: data,
  });
  return result;
};

export const changePassword = (user_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.USER}${user_id}/password/`,
    data: data,
  });
  return result;
};

export const userStateUpdate = (account_id, user_id, data) => {
  let result = axios()({
    method: "put",
    url: `${API.ACCOUNTList}${account_id}/user/${user_id}/`,
    data: data,
  });
  return result;
};

export const userCheckEmail = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/test/email/`,
    data: data,
  });
  return result;
};

/**
 * Serialize query so repeated `status` keys match backend expectations.
 * @param {Record<string, string|number|Array<string|number>>} params
 */
function serializeUserListParams(params) {
  const parts = [];
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === "") continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item == null || item === "") continue;
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`);
      }
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.join("&");
}

/**
 * Builds GET `api/common/v2/user/` query string aligned with production:
 * `?page=1&size=10&limit=10&offset=0&search=…&status=invited&status=operational`
 *
 * @param {{
 *   page?: number,
 *   size?: number,
 *   limit?: number,
 *   offset?: number,
 *   search?: string,
 *   status?: string|string[],
 * }} queryParams
 */
function buildSystemUsersQueryParams(queryParams = {}) {
  let limit = 10;
  if (queryParams.limit != null) {
    limit = Number(queryParams.limit);
  } else if (queryParams.size != null) {
    limit = Number(queryParams.size);
  }
  const page = Number(queryParams.page ?? 1);
  const size = Number(queryParams.size ?? limit);
  const offset = Number(queryParams.offset ?? Math.max(0, (page - 1) * limit));

  const params = {
    page,
    size,
    limit,
    offset,
  };

  const search = queryParams.search;
  if (search != null && String(search).trim() !== "") {
    params.search = String(search).trim();
  }

  const rawStatus = queryParams.status;
  if (rawStatus != null) {
    const list = (Array.isArray(rawStatus) ? rawStatus : [rawStatus])
      .map((s) => (s == null ? "" : String(s).trim()))
      .filter(Boolean);
    if (list.length === 1) {
      params.status = list[0];
    } else if (list.length > 1) {
      params.status = list;
    }
  }

  return params;
}

/**
 * Admin system user list (v2). Sends `page`, `size`, `limit`, `offset`, optional `search`,
 * and repeated `status` for state filters (matches user `state` values: invited, operational, …).
 *
 * @param {{
 *   page?: number,
 *   size?: number,
 *   limit?: number,
 *   offset?: number,
 *   search?: string,
 *   status?: string|string[],
 * }} [queryParams] Omit for a legacy bulk page (first 500 users, unfiltered).
 */
export const getSystemUsers = (queryParams) => {
  const built =
    queryParams != null && typeof queryParams === "object"
      ? buildSystemUsersQueryParams(queryParams)
      : buildSystemUsersQueryParams({
          page: 1,
          size: 500,
          limit: 500,
          offset: 0,
        });

  return axios()({
    method: "get",
    url: `${API.USER_LIST_V2}`,
    params: built,
    paramsSerializer: serializeUserListParams,
  });
};

export const deleteAdminUser = (userId) => {
  let result = axios()({
    method: "delete",
    url: `${API.USER_LIST_V2}${userId}/`,
  });
  return result;
};

/*
  data format: { first_name: String, last_name: String }
 */
export const renameAdminUser = (user_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.USER_LIST_V2}${user_id}/rename/`,
    data: data,
  });
  return result;
};

export const cancelInviteUser = (user_id) => {
  if (user_id == null || user_id === "" || String(user_id).toLowerCase() === "undefined") {
    return Promise.reject(new Error("Missing user id for cancel invite."));
  }
  return axios()({
    method: "post",
    url: `${API.USER_LIST_V2}${user_id}/cancel-invite/`,
    // 204 No Content / empty body should not break JSON parsing
    transformResponse: [
      (data) => {
        if (data == null) return null;
        const s = String(data).trim();
        if (s === "") return null;
        if (s.startsWith("{") || s.startsWith("[")) {
          try {
            return JSON.parse(s);
          } catch {
            return data;
          }
        }
        return data;
      },
    ],
  });
};
export const blockAdminUser = (user_id) => {
  let result = axios()({
    method: "post",
    url: `${API.USER_LIST_V2}${user_id}/block/`,
  });
  return result;
};

export const unBlockAdminUser = (user_id) => {
  let result = axios()({
    method: "post",
    url: `${API.USER_LIST_V2}${user_id}/unblock/`,
  });
  return result;
};

export const closeUser = (user_id) => {
  let result = axios()({
    method: "post",
    url: `${API.USER_LIST_V2}${user_id}/close/`,
  });
  return result;
};

export const unCloseUser = (user_id) => {
  let result = axios()({
    method: "post",
    url: `${API.USER_LIST_V2}${user_id}/unclose/`,
  });
  return result;
};

export const resendInviteAdminUser = (userId, data) => {
  let result = axios()({
    method: "post",
    url: `${API.USER_LIST_V2}${userId}/resend-invite/`,
    data: data,
  });
  return result;
};

export const resetUserPassword = (data) => {
  let result = axios(false)({
    method: "post",
    url: `${API.COMMON_V2}reset-password/`,
    data: data,
    headers: {
      "Content-Type": "application/json",
    },
  });
  return result;
};

export const sendNewPasswordData = (data) => {
  let result = axios(false)({
    method: "post",
    url: `${API.COMMON_V2}user/reset/password/`,
    data: data,
    headers: {
      "Content-Type": "application/json",
    },
  });
  return result;
};

export const signupWithToken = (data) => {
  let result = axios(false)({
    method: "post",
    url: `${API.COMMON_V2}user/0/signup/`,
    data: data,
    headers: {
      "Content-Type": "application/json",
    },
  });
  return result;
};

export const partialUpdateUser = (user_id, data) => {
  let result = axios()({
    method: "patch",
    url: `${API.USER_LIST_V2}${user_id}/`,
    data: data,
  });
  return result;
};

export const getAdminCount = () => {
  return axios()({
    method: "get",
    url: `${API.AdminUserCount}`,
  });
};

export const transferOwnerShip = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/change-owner/`,
    data: data,
  });
  return result;
};

export const userResendInvite = (user_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.USER}${user_id}/resend-invite/`,
    data: data,
  });
  return result;
};

export const userStateUpdateSuspend = (account_id, access_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/access/${access_id}/suspend/`,
    data: data,
  });
  return result;
};

export const userStateUpdateGiveBack = (account_id, access_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/access/${access_id}/un-suspend/`,
    data: data,
  });
  return result;
};

export const startImpersonation = (payload) =>
  axios()({
    method: "post",
    url: API.IMPERSONATE_START,
    data: payload,
  });
